import {Injectable} from '@angular/core';
import {AuthenticationService} from '@services/authentification.service';

export type AiErrorKind = 'ignore' | 'credit' | 'plan' | 'message' | 'generic';

export interface AiError {
    kind: AiErrorKind;
    message: string;
}

export const AI_GENERIC_ERROR = 'L\'analyse n\'a pas pu être réalisée. Aucun crédit ne vous a été décompté, vous pouvez réessayer.';

// Messages techniques renvoyés par l'API : jamais affichables tels quels.
const GENERIC_MESSAGES = [
    'user not found',
    'invalid body',
    'invalid type',
    'invalid objective',
    'ref_ademe is required',
    'favorite not found',
    'ai request failed',
    'cant consume ai credit',
];

// Sur une analyse DPE, une panne du modèle remonte en 400 avec un message technique.
const GENERIC_PREFIXES = ['openai error', 'no response from openai'];

@Injectable({
    providedIn: 'root'
})
export class AiCreditService {

    constructor(private authenticationService: AuthenticationService) {
    }

    get credit(): number {
        const credit = this.authenticationService.currentUser.ai_credit;
        return credit === undefined || credit === null ? 0 : credit;
    }

    // Nombre de crédits rechargés à chaque échéance, 0 si aucun plan ne les fournit.
    get quota(): number {
        const user = this.authenticationService.currentUser;
        if (user.pro_plan !== '') {
            return 100;
        }
        switch (user.plan) {
            case 'trial':
            case 'trial_plus':
            case 'basique':
                return 5;
            case 'partner':
                return 10;
            case 'essentiel':
                return 20;
            case 'expert':
            case 'gold':
            case 'pro':
                return 100;
        }
        return 0;
    }

    // Date de la prochaine recharge, vide pendant l'essai et après la fin d'un abonnement.
    get renewDate(): string {
        const date = this.authenticationService.currentUser.ai_credit_renew_date;
        if (!date || new Date(date).getTime() <= 0 || isNaN(new Date(date).getTime())) {
            return '';
        }
        return date;
    }

    // Un essai terminé laisse ses crédits en base : le solde seul ne suffit pas à autoriser l'analyse.
    get hasPlan(): boolean {
        const user = this.authenticationService.currentUser;
        if (!this.authenticationService.isLogin) {
            return false;
        }
        if (user.pro_plan !== '') {
            return true;
        }
        if (user.subscription && user.subscription.status === 'past_due') {
            return false;
        }
        if (user.plan === 'trial') {
            return this.authenticationService.getTrialDay() > 0;
        }
        if (user.plan === 'trial_plus') {
            return this.authenticationService.getTrialPlusDay() > 0;
        }
        return this.authenticationService.isAllowed('basique');
    }

    get canAnalyze(): boolean {
        return this.hasPlan && this.credit > 0;
    }

    get planUrl(): string {
        return this.authenticationService.currentUser.pro_plan !== '' ? '/app/user/plan-pro' : '/app/user/plan';
    }

    // Le solde renvoyé par l'analyse est celui d'après débit : on l'écrit tel quel.
    applyResponse(response: any): void {
        if (response && response.ai_credit !== undefined && response.ai_credit !== null) {
            this.authenticationService.currentUser.ai_credit = response.ai_credit;
        }
    }

    // Traduit l'erreur d'une analyse en ce qu'il y a à montrer à l'utilisateur.
    handleError(error: any): AiError {
        if (error === 'token_outdate') {
            return {kind: 'ignore', message: ''};
        }

        const status = error && error.status ? error.status : 0;
        const message = this.extractMessage(error);

        if (status === 402) {
            this.authenticationService.currentUser.ai_credit = 0;
            return {kind: 'credit', message: message !== '' ? message : 'Crédits IA épuisés'};
        }
        if (status === 403) {
            return {kind: 'plan', message: message !== '' ? message : 'Abonnement requis pour lancer une analyse IA'};
        }
        if (status !== 500 && this.isDisplayable(message)) {
            return {kind: 'message', message: message};
        }
        return {kind: 'generic', message: AI_GENERIC_ERROR};
    }

    private extractMessage(error: any): string {
        if (typeof error === 'string') {
            return error.trim();
        }
        if (error && error.error && error.error.message) {
            return String(error.error.message).trim();
        }
        return '';
    }

    private isDisplayable(message: string): boolean {
        const text = message.toLowerCase();
        if (text === '') {
            return false;
        }
        if (GENERIC_PREFIXES.some(prefix => text.startsWith(prefix))) {
            return false;
        }
        return GENERIC_MESSAGES.indexOf(text) === -1;
    }
}
